'use client';

import { X } from 'lucide-react';
import { CatalogFiltersState } from './CatalogFilters';
import { useBoatTypes } from '@/hooks/useBoatTypes';

interface Props {
  filters: CatalogFiltersState;
  onChange: (next: CatalogFiltersState) => void;
  onReset: () => void;
  /** Manufacturers/amenities mapping per label-display dei chip. */
  amenityLabels?: Array<{ key: string; label: string }>;
  className?: string;
}

interface Chip {
  id: string;
  label: string;
  remove: () => void;
}

export default function ActiveFilterChips({ filters, onChange, onReset, amenityLabels = [], className = '' }: Props) {
  const { getLabel } = useBoatTypes();
  const amenityMap = new Map(amenityLabels.map((a) => [a.key, a.label]));
  const chips: Chip[] = [];

  // Boat types
  for (const slug of filters.boatTypes) {
    chips.push({
      id: `type-${slug}`,
      label: getLabel(slug) || slug,
      remove: () => onChange({ ...filters, boatTypes: filters.boatTypes.filter((t) => t !== slug) }),
    });
  }

  // Price range
  if (filters.priceMin != null || filters.priceMax != null) {
    const lo = filters.priceMin != null ? `€${filters.priceMin}` : '–';
    const hi = filters.priceMax != null ? `€${filters.priceMax}` : '–';
    chips.push({
      id: 'price',
      label: `Prezzo ${lo}–${hi}`,
      remove: () => onChange({ ...filters, priceMin: null, priceMax: null }),
    });
  }

  // Capacity (mostra solo se diverso da default 0..20)
  if (filters.capacityMin > 2 || filters.capacityMax < 20) {
    chips.push({
      id: 'capacity',
      label: `Capienza ${filters.capacityMin}–${filters.capacityMax}`,
      remove: () => onChange({ ...filters, capacityMin: 2, capacityMax: 20 }),
    });
  }

  // Luogo: porto specifico, città o zona (chip mutuamente esclusivi per chiarezza)
  if (filters.port_id) {
    chips.push({
      id: 'port_id',
      label: filters.where_label || 'Porto selezionato',
      remove: () =>
        onChange({ ...filters, port: null, zone: null, port_id: null, where_label: null }),
    });
  } else {
    if (filters.port) {
      chips.push({
        id: 'port',
        label: filters.port,
        remove: () => onChange({ ...filters, port: null, port_id: null, where_label: null }),
      });
    }
    if (filters.zone) {
      chips.push({
        id: 'zone',
        label: `Zona: ${filters.zone}`,
        remove: () =>
          onChange({ ...filters, zone: null, port: null, port_id: null, where_label: null }),
      });
    }
  }

  // License / skipper / availability / instant booking / cancel / pet / promo / crewed / hostess
  const toggleChips: Array<[keyof CatalogFiltersState, string]> = [
    ['noLicenseOnly', 'Senza patente'],
    ['skipperAvailable', 'Skipper disponibile'],
    ['onlyAvailableInRange', 'Solo disponibili'],
    ['crewedOnly', 'Crewed'],
    ['hostessAvailable', 'Hostess'],
    ['instantBookingOnly', 'Prenotazione istantanea'],
    ['freeCancellation', 'Cancellazione gratuita'],
    ['petFriendly', 'Animali ammessi'],
    ['hasPromo', 'In offerta'],
  ];
  for (const [key, label] of toggleChips) {
    if (filters[key]) {
      chips.push({
        id: String(key),
        label,
        remove: () => onChange({ ...filters, [key]: false } as CatalogFiltersState),
      });
    }
  }

  // Length range
  if (filters.lengthMin != null || filters.lengthMax != null) {
    const lo = filters.lengthMin != null ? `${filters.lengthMin}m` : '–';
    const hi = filters.lengthMax != null ? `${filters.lengthMax}m` : '–';
    chips.push({
      id: 'length',
      label: `Lunghezza ${lo}–${hi}`,
      remove: () => onChange({ ...filters, lengthMin: null, lengthMax: null }),
    });
  }

  // Min cabins / bathrooms
  if ((filters.cabinsMin ?? 0) > 0) {
    chips.push({
      id: 'cabinsMin',
      label: `${filters.cabinsMin}+ cabine`,
      remove: () => onChange({ ...filters, cabinsMin: 0 }),
    });
  }
  if ((filters.bathroomsMin ?? 0) > 0) {
    chips.push({
      id: 'bathroomsMin',
      label: `${filters.bathroomsMin}+ bagni`,
      remove: () => onChange({ ...filters, bathroomsMin: 0 }),
    });
  }

  // Min year
  if (filters.yearMin != null) {
    chips.push({
      id: 'yearMin',
      label: `Anno ≥ ${filters.yearMin}`,
      remove: () => onChange({ ...filters, yearMin: null }),
    });
  }

  // Min rating
  if ((filters.ratingMin ?? 0) > 0) {
    chips.push({
      id: 'ratingMin',
      label: `${filters.ratingMin}★+`,
      remove: () => onChange({ ...filters, ratingMin: 0 }),
    });
  }

  // Manufacturers
  for (const m of filters.manufacturers ?? []) {
    chips.push({
      id: `brand-${m}`,
      label: m,
      remove: () => onChange({ ...filters, manufacturers: (filters.manufacturers ?? []).filter((x) => x !== m) }),
    });
  }

  // Amenities
  for (const k of filters.amenities ?? []) {
    chips.push({
      id: `amenity-${k}`,
      label: amenityMap.get(k) || k,
      remove: () => onChange({ ...filters, amenities: (filters.amenities ?? []).filter((x) => x !== k) }),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`} data-cid="active-filter-chips">
      {chips.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={c.remove}
          className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-800 text-xs font-semibold transition-colors"
        >
          <span>{c.label}</span>
          <X className="w-3 h-3 text-blue-500 group-hover:text-blue-700" />
        </button>
      ))}
      {chips.length > 1 && (
        <button
          type="button"
          onClick={onReset}
          className="ml-1 text-xs font-semibold text-slate-500 hover:text-slate-800 underline underline-offset-2"
        >
          Pulisci tutto
        </button>
      )}
    </div>
  );
}
